import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {applyRedactFont, redactText} from "../../helpers/redact";
import {getTopLevelItems, parseShaperChildrenBlob} from "../../helpers/shaper_children";
import {debugLog} from '../../helpers/debug_log';
import {
    colorForMedianRtt,
    formatMedianRtt,
    formatTrafficVolume,
    medianRttStatusLabel,
    TRAFFIC_OVERVIEW_SHAPER_COLOR,
    shaperChildrenRttToMs,
} from "../../helpers/traffic_overview";

function toNumber(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === "bigint") return Number(value);
    if (value && typeof value === "object" && typeof value.toNumber === "function") {
        return value.toNumber();
    }
    if (typeof value === "number") return value;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function median(values) {
    if (!Array.isArray(values) || values.length === 0) {
        return null;
    }
    const sorted = values
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value))
        .sort((a, b) => a - b);
    if (!sorted.length) {
        return null;
    }
    const middle = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 1) {
        return sorted[middle];
    }
    return (sorted[middle - 1] + sorted[middle]) / 2;
}

function directionCopy(direction) {
    if (direction === "upload") {
        return {
            subtitle: "Traffic leaving this shaper for child branches. Width shows selected-period volume. Color shows median RTT.",
            summary: "Shows where upload traffic leaves this shaper. Wider paths mean more traffic over the selected period, and color shows median RTT status.",
            rootLabel: "Traffic leaving this shaper",
            nodeLabel: "Traffic leaving toward this branch",
        };
    }
    return {
        subtitle: "Traffic arriving at this shaper from child branches. Width shows selected-period volume. Color shows median RTT.",
        summary: "Shows which child branches send the most traffic into this shaper. Wider paths mean more traffic over the selected period, and color shows median RTT status.",
        rootLabel: "Traffic arriving at this shaper",
        nodeLabel: "Traffic arriving from this branch",
    };
}

export class ShaperCombinedSankey extends BaseDashlet {
    constructor(slot, direction = "download") {
        super(slot);
        this.rendered = false;
        this.lastGraphState = null;
        this.direction = direction;
    }

    title() {
        return this.direction === "upload"
            ? "Shaper Children (Upload)"
            : "Shaper Children (Download)";
    }

    supportsZoom() {
        return true;
    }

    tooltip() {
        if (this.direction === "upload") {
            return "<h5>Shaper Children (Upload)</h5><p>Shows where traffic from this shaper is going. Width shows selected-period traffic volume and color shows median RTT status.</p>";
        }
        return "<h5>Shaper Children (Download)</h5><p>Shows which child branches are sending traffic into this shaper. Width shows selected-period traffic volume and color shows median RTT status.</p>";
    }

    buildContainer() {
        const base = super.buildContainer();
        const note = document.createElement("div");
        note.classList.add("px-3", "pb-2", "small", "text-muted");
        note.textContent = directionCopy(this.direction).subtitle;
        base.appendChild(note);
        base.appendChild(this.graphDiv());
        return base;
    }

    setup() {
        super.setup();
        this.graph = new ShaperCombinedSankeyGraph(this.graphDivId(), this.direction);
        if (this.lastGraphState !== null) {
            this.graph.update(this.lastGraphState);
        }
    }

    channelRequests() {
        return ["SiteShaperPage"];
    }

    onMessage(msg) {
        if (msg.Data.target !== "shaper_children") return;

        if (!this.rendered) {
            const myDiv = document.getElementById(this.id);
            myDiv.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            const myDiv = document.getElementById(this.id);
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                myDiv.classList.remove("animate__animated", "animate__pulse");
            }, 1000);
        }

        const parsed = parseShaperChildrenBlob(msg.Data.blob_raw ?? msg.Data.blob);
        window.isFlatNetwork = parsed.isFlat;
        const dataset = getTopLevelItems(parsed);

        const computeStats = (item) => {
            let totalBytes = 0;
            const throughputSeries = this.direction === "upload"
                ? item.body.upload_bytes_max
                : item.body.download_bytes_max;
            (throughputSeries || []).forEach((value) => {
                const n = toNumber(value);
                if (n !== null) {
                    totalBytes += n;
                }
            });

            const rttSeries = [];
            (item.body.median_rtt_median || []).forEach((value) => {
                const rttMs = shaperChildrenRttToMs(toNumber(value));
                if (Number.isFinite(rttMs)) {
                    rttSeries.push(rttMs);
                }
            });

            return {
                value: totalBytes,
                rttMs: median(rttSeries),
            };
        };

        let parents = [];
        let childrenByParent = {};

        if (dataset.isFlat && parsed.circuits) {
            const circuits = dataset.topLevel || {};
            const topList = this.direction === "upload" ? dataset.topUpload : dataset.topDownload;
            const selected = (topList && topList.length ? topList : Object.keys(circuits)) || [];
            selected.forEach((hash) => {
                const item = circuits[hash];
                if (!item) return;
                const stats = computeStats(item);
                if (stats.value <= 0) return;
                parents.push({
                    name: item.header.name,
                    value: stats.value,
                    rttMs: stats.rttMs,
                    site_hash: String(hash),
                });
            });
            parents.sort((a, b) => b.value - a.value);
        } else {
            const topLevelSites = dataset.topLevel || {};
            const childSites = dataset.children || {};
            const childParentMap = dataset.parentMap || {};

            Object.entries(topLevelSites).forEach(([key, item]) => {
                const stats = computeStats(item);
                parents.push({
                    name: item.header.name,
                    value: stats.value,
                    rttMs: stats.rttMs,
                    site_hash: String(key),
                });
            });
            parents.sort((a, b) => b.value - a.value);
            parents = parents.slice(0, 10);

            const parentToChildrenHashes = {};
            Object.entries(childParentMap).forEach(([childHash, parentHash]) => {
                const parentKey = parentHash.toString();
                if (!parentToChildrenHashes[parentKey]) {
                    parentToChildrenHashes[parentKey] = [];
                }
                parentToChildrenHashes[parentKey].push(childHash);
            });

            const childCandidates = [];
            parents.forEach((parent) => {
                const parentKey = String(parent.site_hash);
                const hashes = parentToChildrenHashes[parentKey] || [];

                hashes.forEach((childHash) => {
                    const childItem = childSites[String(childHash)];
                    if (!childItem) {
                        return;
                    }
                    const stats = computeStats(childItem);
                    if (stats.value <= 0) {
                        return;
                    }
                    childCandidates.push({
                        parentHash: parentKey,
                        childHash: String(childHash),
                        name: childItem.header.name,
                        value: stats.value,
                        rttMs: stats.rttMs,
                    });
                });
            });

            childCandidates.sort((a, b) => b.value - a.value);
            const topChildren = childCandidates.slice(0, 10);

            childrenByParent = {};
            topChildren.forEach((child) => {
                if (!childrenByParent[child.parentHash]) {
                    childrenByParent[child.parentHash] = [];
                }
                childrenByParent[child.parentHash].push({
                    name: child.name,
                    value: child.value,
                    rttMs: child.rttMs,
                    site_hash: child.childHash,
                    parent_hash: child.parentHash,
                });
            });
        }

        const graphPayload = {
            direction: this.direction,
            parents,
            childrenByParent,
        };

        this.graph.update(graphPayload);
        this.lastGraphState = graphPayload;
        this.rendered = true;
    }
}

class ShaperCombinedSankeyGraph extends DashboardGraph {
    constructor(id, direction) {
        super(id);
        this.direction = direction;
        this.siteHashMap = {};
        this.lastPayload = null;
        this.option = {
            tooltip: {
                trigger: "item",
                triggerOn: "mousemove",
                formatter: (params) => this.tooltipFormatter(params),
            },
            series: [
                {
                    type: "sankey",
                    data: [],
                    links: [],
                    emphasis: {
                        focus: "adjacency",
                    },
                    lineStyle: {
                        color: "gradient",
                        curveness: 0.5,
                        opacity: 0.8,
                    },
                    label: {
                        color: "#d7dee7",
                    },
                },
            ],
        };
        this.chart.setOption(this.option);

        window.addEventListener("lqos:redact-changed", () => {
            if (this.lastPayload) {
                this.update(this.lastPayload);
            }
        });

        const navigateTo = (siteHash) => {
            if (!siteHash) return;
            const urlParams = new URLSearchParams(window.location.search);
            const nodeId = urlParams.get("shaper_id");
            if (!nodeId) return;
            if (window.isFlatNetwork) {
                window.location.href = `shaper_circuit.html?circuit=${siteHash}&nodeId=${nodeId}`;
            } else {
                window.location.href = `shaper_site.html?site=${siteHash}&nodeId=${nodeId}`;
            }
        };

        this.chart.on("click", (params) => {
            if (params.dataType === "node" && params.name !== "Shaper") {
                navigateTo(this.siteHashMap[params.name]);
            } else if (params.dataType === "edge") {
                const targetNode = params.data.source === "Shaper" ? params.data.target : params.data.source;
                navigateTo(this.siteHashMap[targetNode]);
            }
        });
    }

    tooltipFormatter(params) {
        const copy = directionCopy(this.direction);
        if (params.dataType === "edge") {
            const edge = params.data || {};
            const branchName = edge.source === "Shaper" ? edge.target : edge.source;
            return `
                <strong>${redactText(branchName)}</strong><br/>
                ${copy.nodeLabel}<br/>
                Volume: ${formatTrafficVolume(edge.value)}<br/>
                RTT: ${formatMedianRtt(edge.rttMs)}<br/>
                Status: ${medianRttStatusLabel(edge.rttMs)}
            `;
        }

        if (params.dataType === "node") {
            if (params.name === "Shaper") {
                return `<strong>${redactText(params.name)}</strong><br/>${copy.rootLabel}`;
            }
            const node = params.data || {};
            return `
                <strong>${redactText(params.name)}</strong><br/>
                ${copy.nodeLabel}<br/>
                Volume: ${formatTrafficVolume(node.value)}<br/>
                RTT: ${formatMedianRtt(node.rttMs)}<br/>
                Status: ${medianRttStatusLabel(node.rttMs)}
            `;
        }

        return "";
    }

    update(data) {
        this.chart.hideLoading();
        this.lastPayload = data;
        this.direction = data.direction || this.direction;

        const parents = data.parents || [];
        const childrenByParent = data.childrenByParent || {};
        this.siteHashMap = {};

        const nodeMap = new Map();
        const addNode = (item) => {
            if (!item?.name) return;
            if (!nodeMap.has(item.name)) {
                const label = applyRedactFont({
                    color: "#d7dee7",
                    formatter: (params) => redactText(params.name),
                });
                nodeMap.set(item.name, {
                    name: item.name,
                    value: item.value,
                    rttMs: item.rttMs,
                    itemStyle: {
                        color: item.color,
                        cursor: item.site_hash ? "pointer" : "default",
                    },
                    label,
                });
            }
            if (item.site_hash) {
                this.siteHashMap[item.name] = item.site_hash;
            }
        };

        addNode({
            name: "Shaper",
            value: 0,
            rttMs: null,
            site_hash: null,
            color: TRAFFIC_OVERVIEW_SHAPER_COLOR,
        });

        const links = [];

        parents.forEach((parent) => {
            addNode({
                ...parent,
                color: colorForMedianRtt(parent.rttMs),
            });
            links.push(this.direction === "upload"
                ? {
                    source: "Shaper",
                    target: parent.name,
                    value: parent.value,
                    rttMs: parent.rttMs,
                }
                : {
                    source: parent.name,
                    target: "Shaper",
                    value: parent.value,
                    rttMs: parent.rttMs,
                });
        });

        Object.entries(childrenByParent).forEach(([parentHashStr, children]) => {
            const parent = parents.find((entry) => String(entry.site_hash) === String(parentHashStr));
            if (!parent) return;
            children.forEach((child) => {
                addNode({
                    ...child,
                    color: colorForMedianRtt(child.rttMs),
                });
                links.push(this.direction === "upload"
                    ? {
                        source: parent.name,
                        target: child.name,
                        value: child.value,
                        rttMs: child.rttMs,
                    }
                    : {
                        source: child.name,
                        target: parent.name,
                        value: child.value,
                        rttMs: child.rttMs,
                    });
            });
        });

        this.option.series[0] = {
            ...this.option.series[0],
            data: Array.from(nodeMap.values()),
            links,
            left: "5%",
            right: this.direction === "upload" ? "30%" : "5%",
            top: "5%",
            bottom: "5%",
            nodeWidth: 20,
            nodeGap: 10,
            nodeAlign: this.direction === "upload" ? "right" : "left",
            layoutIterations: 24,
            labelLayout: {
                hideOverlap: true,
            },
        };

        debugLog("sankey nodes", this.option.series[0].data);
        debugLog("sankey links", this.option.series[0].links);
        this.chart.setOption(this.option);
    }
}
